/**
 * Shared utilities for drilldown modals.
 *
 * Consolidates common functions used across DrilldownModal,
 * OTDDrilldownModal, and OTIFDrilldownModal.
 */

import { ExternalLink } from 'lucide-react';
import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface LinkableFieldConfig {
  urlPattern: (value: string, record: Record<string, unknown>) => string;
  tab?: string;
}

export interface TabConfig {
  key: string;
  label: string;
  filter: Record<string, string>;
}

export interface DrilldownField {
  field: string;
  label: string;
}

// =============================================================================
// LINKABLE FIELDS CONFIGURATION
// =============================================================================

/**
 * Common linkable fields shared across all drilldown modals
 */
export const COMMON_LINKABLE_FIELDS: Record<string, LinkableFieldConfig> = {
  grn_number: {
    urlPattern: (value) => `/purchasing?tab=delivery&search=${encodeURIComponent(value)}`,
    tab: 'delivery',
  },
  po_line__po__po_number: {
    // CR-1154: the PO number deep-links to the PO header (the line number links
    // to the PO line item detail).
    urlPattern: (value) => `/purchasing?tab=po_headers&po=${encodeURIComponent(value)}`,
    tab: 'po_headers',
  },
  po_line__line_number: {
    // CR-1154: deep-link to the PO line item detail via its PK. Never key this
    // on `record.id` — that is the metric's base-model PK (GRN / NCR / appeal),
    // so the backend emits an explicit `po_line_id`. Falls back to the
    // PO-filtered list when a record has no resolvable line (e.g. an NCR whose
    // import never matched one).
    urlPattern: (_value, record) => {
      const poLineId = record['po_line_id'];
      if (poLineId != null) {
        return `/purchasing?tab=po_lines&line=${String(poLineId)}`;
      }
      const poNumber = String(record['po_line__po__po_number'] ?? '');
      return `/purchasing?tab=po_lines&search=${encodeURIComponent(poNumber)}`;
    },
    tab: 'po_lines',
  },
  // CR-1154: PO number fields deep-link to the PO header.
  po__po_number: {
    urlPattern: (value) => `/purchasing?tab=po_headers&po=${encodeURIComponent(value)}`,
    tab: 'po_headers',
  },
  po_number: {
    urlPattern: (value) => `/purchasing?tab=po_headers&po=${encodeURIComponent(value)}`,
    tab: 'po_headers',
  },
  grn__po_line__po__po_number: {
    urlPattern: (value) => `/purchasing?tab=po_headers&po=${encodeURIComponent(value)}`,
    tab: 'po_headers',
  },
  ncr_number: {
    urlPattern: (value) => `/quality?tab=ncrs&search=${encodeURIComponent(value)}`,
    tab: 'ncrs',
  },
};

/**
 * Build a deep-link URL to the PO Lines tab for an OTIF drill-down row.
 *
 * CR-4666: Pass `line=<id>` so POLinesTab opens the exact line (was falling
 * back to the first line of the PO).
 * CR-4667: Also pass `supplier=<id>` so the list filter can disambiguate
 * POs whose number collides across different suppliers. Without the
 * supplier id, `po__po_number__icontains=<number>` can resolve to a
 * different PO than the one the user clicked in the drill-down.
 */
function buildOtifPoLinesUrl(record: Record<string, unknown>, poNumber?: string): string {
  const params = new URLSearchParams({ tab: 'po_lines' });
  if (poNumber) params.set('search', poNumber);
  const lineId = String(record['id'] ?? '');
  if (lineId) params.set('line', lineId);
  const supplierId = String(record['po_supplier_id'] ?? '');
  if (supplierId) params.set('supplier', supplierId);
  return `/purchasing?${params.toString()}`;
}

/**
 * OTIF-specific linkable fields
 */
export const OTIF_LINKABLE_FIELDS: Record<string, LinkableFieldConfig> = {
  ...COMMON_LINKABLE_FIELDS,
  line_number: {
    urlPattern: (_value, record) => {
      const poNumber = String(record['po__po_number'] || record['po_line__po__po_number'] || '');
      return buildOtifPoLinesUrl(record, poNumber);
    },
    tab: 'po_lines',
  },
  po__po_number: {
    urlPattern: (value, record) => buildOtifPoLinesUrl(record, String(value || '')),
    tab: 'po_lines',
  },
};

// =============================================================================
// ROW DRILL-THROUGH (CR-7276)
// =============================================================================

/**
 * Where a whole drill-down ROW opens. The individual cells have carried links
 * for a while (PO number → header, line number → line), but the row itself
 * only highlighted on hover and did nothing on click, so a user who clicked a
 * failed line — rather than the small link inside it — got nothing.
 *
 * Priority: the PO line's GRN tab (the receipts are what an investigator is
 * reaching for), then the NCR, then the PO header. `null` means the row has
 * nothing to open and must render with no affordance at all.
 *
 * Never key on `record.id` for the PO line: it is the metric's base-model PK
 * (GRN / NCR / appeal / line) and differs per metric — the backend emits an
 * explicit `po_line_id` for exactly this reason.
 */
export function getRowDrillUrl(record: Record<string, unknown>): string | null {
  const poLineId = record['po_line_id'];
  if (poLineId !== null && poLineId !== undefined && poLineId !== '') {
    return `/purchasing?tab=po_lines&line=${encodeURIComponent(String(poLineId))}&lineTab=grn`;
  }
  const ncrId = record['ncr_id'] ?? (record['ncr_number'] ? record['id'] : undefined);
  if (ncrId !== null && ncrId !== undefined && ncrId !== '') {
    return `/quality/ncr/${encodeURIComponent(String(ncrId))}/edit`;
  }
  const poNumber = record['po_number'] ?? record['po_line__po__po_number'] ?? record['po__po_number'];
  if (poNumber) {
    return `/purchasing?tab=po_headers&po=${encodeURIComponent(String(poNumber))}`;
  }
  return null;
}

/**
 * Opens a drill target exactly as the cell links do: through a real anchor
 * with target="_blank", so the browser treats it as a link the user clicked —
 * a focused new tab, never a popup to block — and the drill-down stays put.
 *
 * CR-7276 rework: this was `window.open(url, '_blank', 'noopener,noreferrer')`.
 * Chrome applies popup rules to window.open that it never applies to a link,
 * and could leave the new tab unfocused, so a row click looked like nothing
 * happened while the blue links in the same row worked.
 */
export function openDrillUrl(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Class to add to a row that has a drill target, so the cursor matches the behaviour. */
export const ROW_DRILL_CLASS = 'cursor-pointer';

/**
 * Props that make a table row a real control: click and keyboard (Enter /
 * Space) both open the target, and the row is reachable by Tab with a role
 * assistive tech understands. Returns nothing for a row with no target, so the
 * affordance and the behaviour can never disagree.
 */
export function rowDrillProps(url: string | null): React.HTMLAttributes<HTMLTableRowElement> {
  if (!url) return {};
  return {
    role: 'link',
    tabIndex: 0,
    title: url.includes('lineTab=grn') ? 'Open the PO line on its GRN tab' : 'Open record',
    onClick: () => openDrillUrl(url),
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDrillUrl(url);
      }
    },
  };
}

// =============================================================================
// VALUE FORMATTING
// =============================================================================

/**
 * Format a value for display in drilldown tables
 */
export function formatDrilldownValue(
  value: unknown,
  // CR-6307: pass the preference-aware formatter (useFormatters().formatDate)
  // so date cells honour the user's date_format with no day-shift. Falls back
  // to a neutral format only if no formatter is supplied.
  formatDate?: (date: string | Date) => string,
): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  // Check if it's a date string (ISO format)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    if (formatDate) return formatDate(value);
    // eslint-disable-next-line no-restricted-syntax -- CR-6307: no-formatter fallback only
    return new Date(value).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return String(value);
}

/**
 * Get status badge styling for OTD status
 */
export function getOTDStatusStyle(status: string): string {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  const styles: Record<string, string> = {
    on_time: 'bg-green-100 text-green-800',
    'on time': 'bg-green-100 text-green-800',
    early: 'bg-blue-100 text-blue-800',
    late: 'bg-red-100 text-red-800',
  };
  return styles[normalizedStatus] || 'bg-gray-100 text-gray-800';
}

/**
 * Get status badge styling for OTIF status
 */
export function getOTIFStatusStyle(value: unknown, lineStatus?: unknown): { className: string; label: string } {
  // METRIC-DEFINITIONS wave 2/3a (R-131): the verdict is three-state. NULL on
  // an OPEN line means the supplier still has time (or is in arrears) — not a
  // verdict at all; NULL on a CLOSED line means no receipt evidence: NOT JUDGED.
  if (value === null || value === undefined || value === '' || value === 'None' || value === 'Not judged') {
    const status = String(lineStatus ?? '').toLowerCase();
    if (status === 'pending' || status === 'partially_received') {
      return { className: 'bg-blue-100 text-blue-800', label: 'OPEN' };
    }
    return { className: 'bg-gray-100 text-gray-700', label: 'NOT JUDGED' };
  }
  // CR-4543: Match actual DB values — 'otif' and 'early' = pass, others = fail
  const isPass = value === true || value === 'True' || value === 'PASS' || value === 'otif' || value === 'early';
  return {
    className: isPass
      ? 'bg-green-100 text-green-800 hover:bg-green-200'
      : 'bg-red-100 text-red-800 hover:bg-red-200',
    label: isPass ? 'PASS' : 'FAILED',
  };
}

/**
 * Get styled label for GRN on_time_status field
 */
export function getOnTimeStatusStyle(value: unknown): { className: string; label: string } {
  const strValue = String(value || '').toLowerCase();
  switch (strValue) {
    case 'on_time':
      return { className: 'bg-green-100 text-green-800', label: 'On Time' };
    case 'early':
      return { className: 'bg-blue-100 text-blue-800', label: 'Early' };
    case 'late':
      return { className: 'bg-red-100 text-red-800', label: 'Late' };
    // CR-6518: overridden verdicts are native statuses, not a flag beside the
    // status. They read green because they score as a pass, but name the
    // verdict they replaced so an accepted exception stays distinguishable
    // from a delivery that met its date on its own merits.
    case 'overridden_early':
      return { className: 'bg-green-100 text-green-800', label: 'Overridden (Early)' };
    case 'overridden_late':
      return { className: 'bg-green-100 text-green-800', label: 'Overridden (Late)' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: value ? String(value) : '-' };
  }
}

// =============================================================================
// CELL RENDERING
// =============================================================================

export interface RenderCellOptions {
  fieldKey: string;
  value: unknown;
  record: Record<string, unknown>;
  linkableFields?: Record<string, LinkableFieldConfig>;
  onStatusClick?: (record: Record<string, unknown>) => void;
  statusField?: string;
}

/**
 * Create a link element for linkable fields.
 *
 * CR-1154: open every deep-link in a new tab (target="_blank"), per the
 * explicit "every hyperlink opens in a new tab" requirement. This reverses
 * CR-5363 round 2, which had made drill-down links same-tab after testers
 * reported the new tab "did nothing" (popup-blocked / opened behind the
 * drilldown). The ExternalLink icon signals the new-tab behaviour.
 */
export function createFieldLink(
  url: string,
  displayValue: string
): React.ReactElement {
  return React.createElement(
    'a',
    {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer',
      className: 'text-primary hover:underline inline-flex items-center gap-1 font-medium',
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    },
    displayValue,
    React.createElement(ExternalLink, { className: 'h-3 w-3' })
  );
}

/**
 * Create an OTD status badge
 */
export function createOTDStatusBadge(value: string): React.ReactElement {
  const { className, label } = getOnTimeStatusStyle(value);
  return React.createElement(
    'span',
    { className: `px-2 py-1 rounded text-xs font-medium ${className}` },
    label
  );
}

/**
 * Create an OTIF status button
 */
export function createOTIFStatusButton(
  value: unknown,
  onClick: () => void
): React.ReactElement {
  const { className, label } = getOTIFStatusStyle(value);
  return React.createElement(
    'button',
    {
      onClick,
      className: `px-2 py-1 rounded text-xs font-medium ${className}`,
    },
    label
  );
}

// =============================================================================
// EXPORT HANDLING
// =============================================================================

/**
 * Download exported data as a file
 */
export function downloadExportedData(
  data: BlobPart,
  filename: string,
  format: 'csv' | 'xlsx'
): void {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// =============================================================================
// TAB CONFIGURATIONS
// =============================================================================

export const OTD_TABS: TabConfig[] = [
  { key: 'on_time', label: 'ON TIME', filter: { otd_status: 'on_time' } },
  { key: 'early', label: 'EARLY', filter: { otd_status: 'early' } },
  { key: 'late', label: 'LATE', filter: { otd_status: 'late' } },
  { key: 'total', label: 'TOTAL', filter: {} },
];

// Mirrors the backend registry (drilldown/registry/metrics_delivery.py) — keep
// the two in step, the labels and filters are duplicated rather than fetched.
export const OTIF_TABS: TabConfig[] = [
  { key: 'failed', label: 'FAILED OTIF LINES', filter: { otif_status: 'failed' } },
  { key: 'arrears', label: 'ARREAR LINES', filter: { otif_status: 'arrears' } },
  { key: 'positive', label: 'PASSED OTIF LINES', filter: { otif_status: 'positive' } },
  // CR-6155: TOTAL excludes arrears, which the ARREAR tab already counts, so
  // TOTAL + ARREAR is the OTIF denominator instead of double-counting every
  // arrear. An empty filter here would show all lines and contradict the ring.
  { key: 'total', label: 'TOTAL LINES', filter: { otif_status: 'total' } },
];
